import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Collapse from '@mui/material/Collapse';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { MODEL_OPTIONS } from 'pages/VideoCreative/models';
import {
  GPT_BRIDGES,
  SCRIPT_VARIABLE,
  composeHookPrompt,
  composeJointHookHeadlinePrompt,
  loadHeadlinePrompt,
  loadHookModel,
  loadHookPrompt,
  saveHeadlinePrompt,
  saveHookModel,
  saveHookPrompt,
} from '../hookSettings';

// Pull the individual hook/headline candidates out of an LLM response. Prefers a
// numbered/bulleted list; falls back to plain non-heading lines (headlines often
// come back as one per line). Strips markdown/quotes and de-dupes.
export function extractOptions(text: string): string[] {
  const lines = (text || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const numbered = lines
    .map((l) => l.match(/^(?:\d+[.)]|[-*•])\s+(.*)$/))
    .filter((m): m is RegExpMatchArray => !!m)
    .map((m) => m[1]);
  const source = numbered.length
    ? numbered
    : lines.filter((l) => !/^#{1,6}\s|^\*\*|^part\b|winner|here are/i.test(l));
  const clean = (s: string) => s
    .replace(/\*\*/g, '')
    .replace(/^\s*[-*•]\s*/, '')
    .replace(/^["'“”]+|["'“”]+$/g, '')
    .trim();
  const out: string[] = [];
  for (const s of source) {
    const c = clean(s);
    if (c && c.length <= 200 && !out.includes(c)) out.push(c);
  }
  return out.slice(0, 12);
}

// One generated section: shows the ideas as a pick-list, lets the user edit the
// chosen one, and saves it straight onto the project (its Hook or Headline).
function ResultPicker({ title, subtitle, text, useLabel, onUse }: {
  title: string; subtitle: string; text: string; useLabel: string; onUse: (value: string) => void;
}) {
  const options = React.useMemo(() => extractOptions(text), [text]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const pick = (opt: string) => { setSelected(opt); setDraft(opt); setSaved(false); };
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" fontWeight={600}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{subtitle}</Typography>
      {options.length > 0 ? (
        <RadioGroup value={selected ?? ''} onChange={(_e, v) => pick(v)}>
          {options.map((opt, i) => (
            <FormControlLabel
              key={i}
              value={opt}
              control={<Radio size="small" sx={{ py: 0.25 }} />}
              label={<Typography variant="body2">{opt}</Typography>}
              sx={{ alignItems: 'flex-start', mb: 0.25 }}
            />
          ))}
        </RadioGroup>
      ) : (
        <Alert severity="info" sx={{ mb: 1 }}>Couldn’t split these automatically — open the full response below and copy one in.</Alert>
      )}
      {selected != null && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label={`Edit ${useLabel} before saving`}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
            fullWidth multiline minRows={1} maxRows={4} size="small"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="contained" size="small" disabled={!draft.trim()} onClick={() => { onUse(draft.trim()); setSaved(true); }}>
              Use as {useLabel}
            </Button>
            {saved && <Typography variant="caption" color="success.main">Saved to the project’s {useLabel}.</Typography>}
          </Box>
        </Box>
      )}
      <Button size="small" onClick={() => setShowRaw((v) => !v)} sx={{ mt: 0.5, textTransform: 'none' }}>
        {showRaw ? 'Hide' : 'Show'} full response
      </Button>
      <Collapse in={showRaw}>
        <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: '#f8fafc', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', maxHeight: 220, overflowY: 'auto', mt: 0.5 }}>
          {text}
        </Box>
      </Collapse>
    </Box>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  // The project's script, pre-filled into the editable Script input. Empty when
  // nothing is transcribed / no script is saved yet — the user can type one.
  initialScript: string;
  onGenerate: (prompt: string, model: string) => Promise<{ text: string } | null>;
  // Persist the (edited/added) script onto the project.
  onSaveScript: (script: string) => void;
  // Save a chosen (and optionally edited) hook / headline onto the project.
  onUseHook: (hook: string) => void;
  onUseHeadline: (headline: string) => void;
  // Load / save the team-shared default prompts (Firebase-backed). load* returns
  // null when none is saved.
  loadSharedHookPrompt: () => Promise<string | null>;
  onSaveSharedHookPrompt: (prompt: string) => Promise<void>;
  loadSharedHeadlinePrompt: () => Promise<string | null>;
  onSaveSharedHeadlinePrompt: (prompt: string) => Promise<void>;
}

// "Hook ideas" for the opening of the video. Generate produces BOTH on-screen
// hook ideas and Meta ad headline ideas from the script; the Default prompt tab
// edits the two reusable templates (each uses {{script}}). Both templates can be
// saved as the shared team default.
export default function HookIdeasDialog({
  open, onClose, initialScript, onGenerate, onSaveScript, onUseHook, onUseHeadline,
  loadSharedHookPrompt, onSaveSharedHookPrompt, loadSharedHeadlinePrompt, onSaveSharedHeadlinePrompt,
}: Props) {
  const [tab, setTab] = useState<'generate' | 'template'>('generate');
  const [hookInstructions, setHookInstructions] = useState(loadHookPrompt);
  const [headlineInstructions, setHeadlineInstructions] = useState(loadHeadlinePrompt);
  const [model, setModel] = useState(loadHookModel);
  const [script, setScript] = useState(initialScript);
  const [loading, setLoading] = useState(false);
  const [hookResult, setHookResult] = useState<string | null>(null);
  const [headlineResult, setHeadlineResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [defaultSaved, setDefaultSaved] = useState(false);

  // Templates + model persist as sticky per-browser prefs; the shared team
  // defaults (Firebase) override on open.
  useEffect(() => { saveHookPrompt(hookInstructions); }, [hookInstructions]);
  useEffect(() => { saveHeadlinePrompt(headlineInstructions); }, [headlineInstructions]);
  useEffect(() => { saveHookModel(model); }, [model]);
  useEffect(() => { if (open) setScript(initialScript); }, [open, initialScript]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadSharedHookPrompt().then(shared => {
      if (!cancelled && shared && shared.trim()) { setHookInstructions(shared); saveHookPrompt(shared); }
    }).catch(() => { /* offline / not deployed — keep local default */ });
    loadSharedHeadlinePrompt().then(shared => {
      if (!cancelled && shared && shared.trim()) { setHeadlineInstructions(shared); saveHeadlinePrompt(shared); }
    }).catch(() => { /* keep local default */ });
    return () => { cancelled = true; };
  }, [open, loadSharedHookPrompt, loadSharedHeadlinePrompt]);

  const hasScript = !!script.trim();
  const hookPrompt = composeHookPrompt(hookInstructions, script);
  const headlinePrompt = composeHookPrompt(headlineInstructions, script);

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
    } catch { /* clipboard blocked — user can still select the text */ }
  }, []);

  function changeScript(value: string) {
    setScript(value);
    onSaveScript(value);
  }

  async function runGenerate() {
    if (!hasScript) return;
    setLoading(true);
    setError(null);
    setHookResult(null);
    setHeadlineResult(null);
    try {
      const [hooks, headlines] = await Promise.all([
        onGenerate(hookPrompt, model),
        onGenerate(headlinePrompt, model),
      ]);
      if (hooks?.text) setHookResult(hooks.text);
      if (headlines?.text) setHeadlineResult(headlines.text);
      if (!hooks?.text && !headlines?.text) setError('Nothing came back — try again or pick another model.');
    } catch {
      setError('Could not generate. Check the connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function openBridge(url: string, label: string) {
    // Copy a JOINT hook + headline prompt so the GPT returns both in one paste.
    void copyToClipboard(composeJointHookHeadlinePrompt(hookInstructions, headlineInstructions, script), label);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function saveAsTeamDefault() {
    setSavingDefault(true);
    setDefaultSaved(false);
    try {
      await Promise.all([onSaveSharedHookPrompt(hookInstructions), onSaveSharedHeadlinePrompt(headlineInstructions)]);
      setDefaultSaved(true);
      setTimeout(() => setDefaultSaved(false), 2500);
    } catch { /* surfaced by the service snackbar */ } finally {
      setSavingDefault(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        Hook &amp; headline ideas
        <IconButton aria-label="Close hook ideas" onClick={onClose} sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab value="generate" label="Generate" sx={{ textTransform: 'none' }} />
        <Tab value="template" label="Default prompts" sx={{ textTransform: 'none' }} />
      </Tabs>

      {tab === 'generate' ? (
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>Model</Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select value={model} onChange={(e) => setModel(e.target.value)} disabled={loading}>
                {MODEL_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {GPT_BRIDGES.map((b) => (
                <Tooltip key={b.url} title={`Copies a combined hook + headline prompt (with the script), then opens ${b.label} in a new tab — paste it in to get both back (custom GPTs can't be pre-filled from a link)`}>
                  <span>
                    <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} onClick={() => openBridge(b.url, b.label)} disabled={!hasScript}>
                      {b.label}
                    </Button>
                  </span>
                </Tooltip>
              ))}
            </Box>
          </Box>

          <TextField
            label="Script"
            value={script}
            onChange={(e) => changeScript(e.target.value)}
            fullWidth multiline minRows={5} maxRows={14}
            disabled={loading}
            placeholder="Paste or type the video's script. Saved to the project."
            helperText={hasScript ? 'Saved to the project. Generates both hook and headline ideas. Edit the templates in “Default prompts”.' : 'No script yet — add one here (it saves to the project).'}
            sx={{ mb: 1 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Copy the hook instructions + script to paste anywhere (e.g. into a custom GPT)">
                <span>
                  <Button size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={() => copyToClipboard(hookPrompt, 'the clipboard')} disabled={!hasScript}>
                    Copy hook prompt
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Copy the headline instructions + script to paste anywhere (e.g. into a custom GPT)">
                <span>
                  <Button size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={() => copyToClipboard(headlinePrompt, 'the clipboard')} disabled={!hasScript}>
                    Copy headline prompt
                  </Button>
                </span>
              </Tooltip>
              {copied && <Typography variant="caption" color="success.main">Copied — paste it into {copied}</Typography>}
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={runGenerate}
              disabled={loading || !hasScript}
            >
              {loading ? 'Generating…' : 'Generate hooks + headlines'}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {hookResult && (
            <ResultPicker
              key={`hook:${hookResult}`}
              title="Hook ideas (opening on-screen text)"
              subtitle="Pick one, tweak it if you like, then save it as the project’s hook."
              text={hookResult}
              useLabel="hook"
              onUse={onUseHook}
            />
          )}
          {headlineResult && (
            <ResultPicker
              key={`headline:${headlineResult}`}
              title="Headline ideas (Meta ad headlines)"
              subtitle="Pick one, tweak it if you like, then save it as the project’s headline."
              text={headlineResult}
              useLabel="headline"
              onUse={onUseHeadline}
            />
          )}
          {(hookResult || headlineResult) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Saved hooks/headlines appear in the Ad copy panel beside the video.
            </Typography>
          )}
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The reusable instruction templates. Each uses <code>{SCRIPT_VARIABLE}</code> where the script is inserted
            (removed → the script is appended). “Save as team default” stores both for everyone; editing here alone only
            affects this browser.
          </Typography>
          <TextField
            label="Hook prompt"
            value={hookInstructions}
            onChange={(e) => setHookInstructions(e.target.value)}
            fullWidth multiline minRows={6} maxRows={16}
            InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' } }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Headline prompt"
            value={headlineInstructions}
            onChange={(e) => setHeadlineInstructions(e.target.value)}
            fullWidth multiline minRows={6} maxRows={16}
            InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
            <Button
              variant="contained"
              startIcon={savingDefault ? <CircularProgress size={16} color="inherit" /> : undefined}
              disabled={savingDefault || !hookInstructions.trim() || !headlineInstructions.trim()}
              onClick={saveAsTeamDefault}
            >
              Save as team default
            </Button>
            {defaultSaved && <Typography variant="caption" color="success.main">Saved — everyone gets these defaults now.</Typography>}
          </Box>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
